import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { api } from '../../api/client';
import styles from './AnalyticsPage.module.css';

const getSalesData = (from: string, to: string) =>
  api.get('/admin/analytics/sales', { params: { from, to } }).then(r => r.data.data);
const getTopProducts = () => api.get('/admin/analytics/products').then(r => r.data.data);
const getSummary = () => api.get('/admin/analytics').then(r => r.data.data);

export default function AnalyticsPage() {
  const { t } = useTranslation();
  const today = new Date().toISOString().slice(0, 10);
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(thirtyDaysAgo);
  const [to, setTo] = useState(today);

  const { data: summary } = useQuery({ queryKey: ['analytics-summary'], queryFn: getSummary });
  const { data: salesData } = useQuery({ queryKey: ['analytics-sales', from, to], queryFn: () => getSalesData(from, to) });
  const { data: topData } = useQuery({ queryKey: ['analytics-products'], queryFn: getTopProducts });

  const salesChart = (salesData?.sales || []).sort((a: { date: string }, b: { date: string }) => a.date.localeCompare(b.date));
  const topProducts = (topData?.products || []).slice(0, 10);

  return (
    <div>
      <h1 className={styles.pageTitle}>{t('admin.analytics')}</h1>

      {/* Summary cards */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Total Revenue</div>
          <div className={styles.statValue}>₹{summary?.totalRevenue?.toLocaleString('en-IN') || '0'}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Total Orders</div>
          <div className={styles.statValue}>{summary?.totalOrders || 0}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Last 30 Days Revenue</div>
          <div className={styles.statValue}>₹{summary?.recentRevenue?.toLocaleString('en-IN') || '0'}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Last 30 Days Orders</div>
          <div className={styles.statValue}>{summary?.recentOrders || 0}</div>
        </div>
      </div>

      {/* Sales chart */}
      <div className={styles.chartCard}>
        <div className={styles.chartHeader}>
          <h2 className={styles.chartTitle}>Revenue Over Time</h2>
          <div className={styles.dateRange}>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} className={styles.dateInput} />
            <span>to</span>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} className={styles.dateInput} />
          </div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={salesChart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8B1A1A" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#8B1A1A" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0e8de" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `₹${v}`} />
            <Tooltip formatter={(v: number) => [`₹${v.toLocaleString('en-IN')}`, 'Revenue']} />
            <Area type="monotone" dataKey="revenue" stroke="#8B1A1A" fill="url(#revenueGrad)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Top products */}
      <div className={styles.chartCard}>
        <h2 className={styles.chartTitle}>Top Selling Products</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={topProducts} layout="vertical" margin={{ left: 120, right: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0e8de" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={v => `₹${v}`} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={110} />
            <Tooltip formatter={(v: number) => [`₹${v.toLocaleString('en-IN')}`, 'Revenue']} />
            <Bar dataKey="revenue" fill="#C9922A" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
